import { useState, useEffect } from "react";
import { useApi } from "@/lib/api";
import { toast } from "sonner";
import { 
  BarChart3, 
  TrendingUp, 
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  FileDown,
  Save,
  FolderOpen,
  Trash2,
  Clock,
  X
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { downloadGapAnalysisPDF } from "@/utils/pdfGenerator";

export default function GapAnalysis() {
  const api = useApi();
  const [platforms, setPlatforms] = useState([]);
  const [selectedPlatform, setSelectedPlatform] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  
  // Save functionality
  const [savedAnalyses, setSavedAnalyses] = useState([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveTitle, setSaveTitle] = useState("");
  const [savingAnalysis, setSavingAnalysis] = useState(false);

  useEffect(() => {
    fetchPlatforms();
    fetchSavedAnalyses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPlatforms = async () => {
    try {
      const response = await api.get("/api/platforms");
      setPlatforms(response.data);
    } catch (error) {
      console.error("Error fetching platforms:", error);
    } finally {
      setInitialLoading(false);
    }
  };

  const fetchSavedAnalyses = async () => {
    try {
      const response = await api.get("/api/saved-analyses/");
      setSavedAnalyses(response.data?.analyses || []);
    } catch (error) {
      console.error("Error fetching saved analyses:", error);
    }
  };

  const runAnalysis = async () => {
    if (!selectedPlatform) {
      toast.error("Please select a platform");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post("/api/analysis/gap-analysis", {
        platform_id: selectedPlatform
      });
      setAnalysis(response.data);
      toast.success("Gap analysis completed");
    } catch (error) {
      console.error("Error running analysis:", error);
      toast.error("Failed to run analysis");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAnalysis = async () => {
    if (!analysis) return;
    
    setSavingAnalysis(true);
    try {
      await api.post("/api/saved-analyses/", {
        platform_id: selectedPlatform,
        platform_name: analysis.platform,
        analysis_data: analysis,
        title: saveTitle || `${analysis.platform} Analysis`
      });
      toast.success("Analysis saved");
      setShowSaveDialog(false);
      setSaveTitle("");
      fetchSavedAnalyses();
    } catch (error) {
      console.error("Error saving analysis:", error);
      toast.error("Failed to save analysis");
    } finally {
      setSavingAnalysis(false);
    }
  };

  const loadSavedAnalysis = (saved) => {
    setAnalysis(saved.analysis_data);
    setSelectedPlatform(saved.platform_id);
    toast.success(`Loaded: ${saved.title}`);
  };

  const deleteSavedAnalysis = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this saved analysis?")) return;
    
    try {
      await api.delete(`/api/saved-analyses/${id}`);
      toast.success("Analysis deleted");
      fetchSavedAnalyses();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  if (initialLoading) {
    return (
      <div className="content-wrapper flex items-center justify-center min-h-[50vh]">
        <div className="loading-spinner" data-testid="loading-spinner"></div>
      </div>
    );
  }

  const inHousePercentage = analysis 
    ? Math.round((analysis.in_house_count / analysis.total_categories) * 100)
    : 0;

  const gapPercentage = analysis
    ? Math.round((analysis.gap_count / analysis.total_categories) * 100)
    : 0;

  const handleDownloadPDF = () => {
    if (!analysis) return;
    try {
      const filename = downloadGapAnalysisPDF(analysis);
      toast.success(`Downloaded ${filename}`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF");
    }
  };

  return (
    <div className="content-wrapper" data-testid="gap-analysis-page">
      <div className="page-header compact">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Gap Analysis</h1>
            <p className="page-subtitle">
              Identify in-house vs. outsource opportunities
            </p>
          </div>
          
          {/* Saved Analyses Sheet */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm" data-testid="open-saved-btn">
                <FolderOpen className="w-4 h-4 mr-1.5" />
                Saved ({savedAnalyses.length})
              </Button>
            </SheetTrigger>
            <SheetContent className="w-[350px] bg-white border-l border-gray-200">
              <SheetHeader>
                <SheetTitle className="text-base text-gray-900">Saved Analyses</SheetTitle>
              </SheetHeader>
              <div className="mt-3 space-y-2">
                {savedAnalyses.length === 0 ? (
                  <div className="text-center py-6 text-gray-400">
                    <FolderOpen className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No saved analyses yet</p>
                  </div>
                ) : (
                  savedAnalyses.map(saved => (
                    <div
                      key={saved.id}
                      onClick={() => loadSavedAnalysis(saved)}
                      className="p-2.5 border border-gray-200 rounded-lg cursor-pointer hover:border-violet-500 hover:bg-violet-500/5 transition-colors group"
                      data-testid={`saved-analysis-${saved.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 text-sm truncate">{saved.title}</h4>
                          <p className="text-xs text-gray-500">{saved.platform_name}</p>
                          <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-gray-400">
                            <Clock className="w-3 h-3" />
                            {formatDate(saved.created_at)}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300"
                          onClick={(e) => deleteSavedAnalysis(saved.id, e)}
                          data-testid={`delete-saved-${saved.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      <div className="flex gap-1.5 mt-1.5">
                        <Badge variant="secondary" className="text-[10px] bg-violet-500/15 text-violet-500">
                          {saved.analysis_data?.gap_count || 0} gaps
                        </Badge>
                        <Badge variant="outline" className="text-[10px] border-gray-200 text-gray-500">
                          {saved.analysis_data?.in_house_count || 0} in-house
                        </Badge>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Analysis Controls - Compact */}
      <div className="card p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
          <div className="flex-1 w-full sm:w-auto">
            <label className="block text-xs font-medium text-gray-500 mb-1.5">
              Select Platform
            </label>
            <Select value={selectedPlatform} onValueChange={setSelectedPlatform}>
              <SelectTrigger className="w-full h-9 bg-gray-50 border-gray-200 text-gray-900" data-testid="platform-select">
                <SelectValue placeholder="Choose a platform" />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                {platforms.map((platform) => (
                  <SelectItem key={platform.id} value={platform.id} className="text-gray-900 hover:bg-gray-100">
                    {platform.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <button
            onClick={runAnalysis}
            disabled={loading || !selectedPlatform}
            className="btn-primary whitespace-nowrap"
            data-testid="run-analysis-btn"
          >
            {loading ? (
              <>
                <div className="loading-spinner w-4 h-4"></div>
                <span>Analyzing...</span>
              </>
            ) : (
              <>
                <BarChart3 size={16} />
                <span>Run Analysis</span>
              </>
            )}
          </button>
          {analysis && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSaveTitle(`${analysis.platform} Analysis`);
                  setShowSaveDialog(true);
                }}
                className="whitespace-nowrap"
                data-testid="save-analysis-btn"
              >
                <Save size={14} className="mr-1" />
                Save
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadPDF}
                className="whitespace-nowrap"
                data-testid="download-gap-pdf"
              >
                <FileDown size={14} className="mr-1" />
                PDF
              </Button>
            </>
          )}
        </div>
      </div>

      {analysis && (
        <div className="space-y-4 animate-fade-in">
          {/* Summary Stats - Compact */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="stat-card">
              <div className="stat-card-header">
                <span className="stat-card-title">Total Categories</span>
                <div className="stat-card-icon blue">
                  <BarChart3 size={14} />
                </div>
              </div>
              <div className="stat-card-value">{analysis.total_categories}</div>
              <div className="stat-card-subtitle">Compliance areas</div>
            </div>

            <div className="stat-card">
              <div className="stat-card-header">
                <span className="stat-card-title">In-House</span>
                <div className="stat-card-icon green">
                  <CheckCircle size={14} />
                </div>
              </div>
              <div className="stat-card-value text-emerald-500">{analysis.in_house_count}</div>
              <div className="stat-card-subtitle">Platform handles</div>
            </div>

            <div className="stat-card">
              <div className="stat-card-header">
                <span className="stat-card-title">Service Gaps</span>
                <div className="stat-card-icon purple">
                  <TrendingUp size={14} />
                </div>
              </div>
              <div className="stat-card-value text-violet-500">{analysis.gap_count}</div>
              <div className="stat-card-subtitle">Outsource opportunities</div>
            </div>
          </div>

          {/* Visual Breakdown - Compact */}
          <div className="card p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">
              Capability Breakdown: {analysis.platform}
            </h3>
            
            <div className="space-y-3">
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-medium text-emerald-500">In-House</span>
                  <span className="text-xs text-gray-500">{inHousePercentage}%</span>
                </div>
                <Progress value={inHousePercentage} className="h-2 bg-white/10" />
              </div>

              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-medium text-violet-500">Opportunities</span>
                  <span className="text-xs text-gray-500">{gapPercentage}%</span>
                </div>
                <Progress value={gapPercentage} className="h-2 bg-white/10 [&>div]:bg-violet-500" />
              </div>
            </div>
          </div>

          {/* Service Opportunities - Compact */}
          <div className="card p-4">
            <div className="flex items-center gap-1.5 mb-3">
              <TrendingUp size={16} className="text-violet-500" />
              <h3 className="text-sm font-semibold text-gray-900">
                Opportunities ({analysis.service_opportunities.length})
              </h3>
            </div>
            
            <div className="overflow-x-auto">
              <table className="gap-table" data-testid="opportunities-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Description</th>
                    <th>Complexity</th>
                    <th>Opportunity</th>
                    <th>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {analysis.service_opportunities.map((opp, idx) => (
                    <tr key={idx}>
                      <td className="font-medium text-xs">
                        {opp.category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </td>
                      <td className="text-gray-500 text-xs">{opp.description}</td>
                      <td>
                        <span className={`gap-badge ${
                          opp.complexity === 'high' ? 'risk' : 
                          opp.complexity === 'medium' ? 'opportunity' : 'in-house'
                        }`}>
                          {opp.complexity}
                        </span>
                      </td>
                      <td>
                        <span className={`gap-badge ${
                          opp.opportunity_level === 'full' ? 'opportunity' : 'in-house'
                        }`}>
                          {opp.opportunity_level}
                        </span>
                      </td>
                      <td>
                        <span className={`gap-badge ${
                          opp.revenue_potential === 'high' ? 'opportunity' : 'in-house'
                        }`}>
                          {opp.revenue_potential}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Risk Areas - Compact */}
          {analysis.risk_areas.length > 0 && (
            <div className="card p-4">
              <div className="flex items-center gap-1.5 mb-3">
                <AlertTriangle size={16} className="text-amber-500" />
                <h3 className="text-sm font-semibold text-gray-900">
                  Priority Areas ({analysis.risk_areas.length})
                </h3>
              </div>
              
              <div className="space-y-2">
                {analysis.risk_areas.map((risk, idx) => (
                  <div 
                    key={idx}
                    className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-gray-900 text-sm">
                        {risk.category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                      <span className={`gap-badge ${
                        risk.risk_level === 'high' ? 'risk' : 'opportunity'
                      }`}>
                        {risk.risk_level} priority
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mb-1">{risk.description}</p>
                    <div className="flex items-center gap-1.5 text-xs text-violet-500">
                      <ArrowRight size={12} />
                      <span>{risk.recommendation}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {!analysis && !loading && (
        <div className="empty-state">
          <BarChart3 size={48} className="mx-auto mb-4 text-gray-300" />
          <h3>No Analysis Yet</h3>
          <p>Select a platform and run the analysis to identify service opportunities</p>
        </div>
      )}

      {/* Save Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Save Gap Analysis</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium text-gray-500">Title</label>
            <Input
              value={saveTitle}
              onChange={(e) => setSaveTitle(e.target.value)}
              placeholder="E.g., Amazon Q1 2026 Analysis"
              className="mt-2"
              data-testid="save-title-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveDialog(false)} className="border-gray-200 text-gray-600 hover:bg-gray-100">Cancel</Button>
            <Button onClick={handleSaveAnalysis} disabled={savingAnalysis} data-testid="confirm-save-btn" className="bg-violet-500 hover:bg-violet-600">
              {savingAnalysis ? "Saving..." : "Save Analysis"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
